import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { UserPlus, Shield, Briefcase, ClipboardCheck, User, Check, X, MoreVertical, RefreshCw } from 'lucide-react';

const ROLE_ICONS = { admin: Shield, lawyer: Briefcase, agent: ClipboardCheck, client: User };
const ROLE_COLORS = { admin: 'bg-black text-white', lawyer: 'bg-[#002FA7] text-white', agent: 'bg-[#2A9D8F] text-white', client: 'bg-gray-200 text-gray-700' };
const STATUS_COLORS = { active: 'bg-green-100 text-green-700', inactive: 'bg-gray-100 text-gray-700', suspended: 'bg-red-100 text-red-700' };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', password: '', role: 'lawyer', mobile: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [actionMenu, setActionMenu] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await api.post('/admin/users', form);
      setShowCreate(false);
      setForm({ full_name: '', email: '', password: '', role: 'lawyer', mobile: '' });
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/approve`);
      await loadUsers();
    } catch (err) { console.error(err); }
    setActionMenu(null);
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Suspend this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      await loadUsers();
    } catch (err) { console.error(err); }
    setActionMenu(null);
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}`, { role: newRole });
      await loadUsers();
    } catch (err) { console.error(err); }
    setActionMenu(null);
  };

  const filteredUsers = roleFilter ? users.filter(u => u.role === roleFilter) : users;

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} total users</p>
        </div>
        <button data-testid="create-user-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-black text-white text-sm rounded-sm hover:bg-gray-800 transition">
          <UserPlus className="w-4 h-4" /> New User
        </button>
      </div>

      {/* Role Filter Tabs */}
      <div className="flex gap-2 mb-5">
        {['', 'admin', 'lawyer', 'agent', 'client'].map(r => (
          <button key={r} onClick={() => setRoleFilter(r)} data-testid={`filter-${r || 'all'}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase rounded-sm border transition ${
              roleFilter === r ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {r || 'All'} {r ? `(${users.filter(u => u.role === r).length})` : `(${users.length})`}
          </button>
        ))}
      </div>

      {/* Users Table */}
      <div className="border border-gray-200">
        <table className="w-full" data-testid="users-table">
          <thead>
            <tr className="border-b border-gray-200 bg-surface">
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">User</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Email</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Role</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Created</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => {
              const Icon = ROLE_ICONS[u.role] || User;
              return (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-sm flex items-center justify-center text-xs font-bold ${ROLE_COLORS[u.role]}`}>
                        {u.full_name?.[0] || '?'}
                      </div>
                      {u.full_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${ROLE_COLORS[u.role]}`}>
                      <Icon className="w-3 h-3" /> {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${STATUS_COLORS[u.status] || 'bg-gray-100'}`}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 relative">
                    <button data-testid={`user-actions-${u.id}`} onClick={() => setActionMenu(actionMenu === u.id ? null : u.id)}
                      className="p-1 hover:bg-gray-200 rounded transition"><MoreVertical className="w-4 h-4 text-gray-500" /></button>
                    {actionMenu === u.id && (
                      <div className="absolute right-4 top-10 bg-white border border-gray-200 shadow-lg rounded-sm z-20 w-44" data-testid={`action-menu-${u.id}`}>
                        {u.status !== 'active' && (
                          <button onClick={() => handleApprove(u.id)} data-testid={`approve-${u.id}`}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 text-green-700 flex items-center gap-2">
                            <Check className="w-3.5 h-3.5" /> Approve / Activate
                          </button>
                        )}
                        {u.status === 'active' && u.role !== 'admin' && (
                          <button onClick={() => handleSuspend(u.id)} data-testid={`suspend-${u.id}`}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-700 flex items-center gap-2">
                            <X className="w-3.5 h-3.5" /> Suspend
                          </button>
                        )}
                        {u.role !== 'admin' && (
                          <>
                            <div className="border-t border-gray-100" />
                            <div className="px-3 py-1.5 text-[10px] font-mono uppercase text-gray-400">Change Role</div>
                            {['lawyer', 'agent', 'client'].filter(r => r !== u.role).map(r => (
                              <button key={r} onClick={() => handleUpdateRole(u.id, r)}
                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 capitalize">{r}</button>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)} data-testid="create-user-modal">
          <div className="bg-white w-full max-w-md border border-gray-200 rounded-sm" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-heading text-lg font-semibold">Onboard New User</h3>
              <p className="text-xs text-gray-500 mt-0.5">Create a new lawyer, agent, or admin account</p>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{error}</div>}
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5">Full Name</label>
                <input data-testid="new-user-name" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-black focus:outline-none" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5">Email</label>
                <input data-testid="new-user-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-black focus:outline-none" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5">Password</label>
                <input data-testid="new-user-password" type="password" required minLength={6} value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-black focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5">Role</label>
                  <select data-testid="new-user-role" value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm">
                    <option value="lawyer">Lawyer</option>
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-1.5">Mobile</label>
                  <input data-testid="new-user-mobile" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-black focus:outline-none" />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
                <button data-testid="create-user-submit" type="submit" disabled={creating}
                  className="px-4 py-2 text-sm bg-black text-white rounded-sm hover:bg-gray-800 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create & Onboard'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
